import { Popover, Table, Tag, Tooltip } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import React, { useMemo } from 'react';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DAY_FORMAT, DAYTIME_FORMAT, ORDER_STATUSES, ORDER_TYPES } from '../../constants';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import useColumnConfig from '../../hooks/useColumnConfig';
import ColumnConfigDrawer from '../ColumnConfigDrawer';

const MOBILE_HIDDEN_KEYS = [
  'pickupAddress',
  'deliveryAddress',
  'pickupTimeWindow',
  'deliveryTimeWindow',
  'volume',
  'partner',
  'deliveryPhone',
  'orderType',
  'collectedItems',
];

const FIXED_KEYS = ['orderCode', 'status', 'action'];

export default function OrdersTable({
  withAction,
  dataSource,
  selectedRowKeys,
  onSelectChange,
  configRef,
  onPageSizeChange: onPageSizeChangeProp,
  linkPrefix = '/admin/orders',
  ...props
}) {
  const isMobile = useMediaQuery('(max-width: 767px)');
  const { t } = useTranslation('orders');
  const { t: tc } = useTranslation('common');

  const allColumns = useMemo(() => {
    const cols = [
      {
        title: t('orderId'),
        dataIndex: 'orderCode',
        key: 'orderCode',
        fixed: 'left',
        width: 200,
        className: 'border-right',
        render: (value) => (
          <Link to={`${linkPrefix}/${value}`} style={{ color: '#1677ff' }}>
            {value}
          </Link>
        ),
      },
      {
        title: <Tooltip title={t('orderRefTooltip')}>{t('orderRef')}</Tooltip>,
        dataIndex: 'orderRef',
        key: 'orderRef',
        width: 200,
        ellipsis: true,
      },
      {
        title: <Tooltip title={t('partnerOrderRefTooltip')}>{t('partnerOrderRef')}</Tooltip>,
        dataIndex: 'partnerOrderRef',
        key: 'partnerOrderRef',
        width: 200,
        ellipsis: true,
      },
      {
        title: t('partner'),
        dataIndex: 'partner',
        key: 'partner',
        width: 200,
        render: (value, record) => value || record?.partnerRef?.name || '',
      },
      {
        title: t('deliveryDate'),
        dataIndex: 'deliveryDate',
        key: 'deliveryDate',
        sorter: true,
        render: (value) => {
          return moment(value).format(DAY_FORMAT);
        },
      },
      {
        title: t('deliveryName'),
        dataIndex: 'name',
        key: 'name',
        width: 200,
      },
      {
        title: t('deliveryAddress'),
        dataIndex: 'deliveryAddress',
        key: 'deliveryAddress',
        width: 300,
        ellipsis: true,
      },
      {
        title: t('routeId'),
        dataIndex: 'routeId',
        key: 'routeId',
        width: 200,
      },
      {
        title: tc('type'),
        dataIndex: 'orderType',
        key: 'orderType',
        width: 140,
        render: (value) => {
          if (value === ORDER_TYPES.SERVICE) {
            return <Tag color="orange">{tc(`orderTypes.${value}`)}</Tag>;
          }
          return <Tag>{tc(`orderTypes.${value}`) || tc('orderTypes.STANDARD')}</Tag>;
        },
      },
      {
        title: (
          <Tooltip title={t('typesTooltip')}>
            <span style={{ borderBottom: '1px dashed #999', cursor: 'help' }}>{t('types')}</span>
          </Tooltip>
        ),
        dataIndex: 'types',
        key: 'types',
        width: 200,
        render: (value) => {
          if (!value || !value.length) return '-';
          return value.map((type) => (
            <Tag key={type} style={{ marginBottom: 2 }}>
              {type}
            </Tag>
          ));
        },
      },
      {
        title: tc('createdAt'),
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 250,
        render: (value) => {
          return value ? moment(value).format(DAYTIME_FORMAT) : '';
        },
      },
      {
        title: t('pickupAddress'),
        dataIndex: 'pickupAddress',
        key: 'pickupAddress',
        width: 300,
        ellipsis: true,
      },
      {
        title: t('deliveryPhone'),
        dataIndex: 'deliveryPhone',
        key: 'deliveryPhone',
        width: 200,
      },
      {
        title: t('pickupTimeWindow'),
        dataIndex: 'pickupTimeWindow',
        key: 'pickupTimeWindow',
        width: 200,
        ellipsis: true,
        render(_value, record) {
          return (record.pickupTimeStart || '') + ' - ' + (record.pickupTimeEnd || '');
        },
      },
      {
        title: t('deliveryTimeWindow'),
        dataIndex: 'deliveryTimeWindow',
        key: 'deliveryTimeWindow',
        width: 200,
        ellipsis: true,
        render(_value, record) {
          return (record.deliveryTimeStart || '') + ' - ' + (record.deliveryTimeEnd || '');
        },
      },
      {
        title: tc('volume'),
        dataIndex: 'volume',
        key: 'volume',
        width: 200,
      },
      {
        title: tc('updatedAt'),
        dataIndex: 'updatedAt',
        key: 'updatedAt',
        width: 250,
        render: (value) => {
          return value ? moment(value).format(DAYTIME_FORMAT) : '';
        },
      },
      {
        title: t('collected'),
        key: 'collectedItems',
        width: 90,
        align: 'center',
        render: (_, record) => {
          const count = record._count?.collectedItems || 0;
          return count > 0 ? (
            <Tag icon={<InboxOutlined />} color="orange">
              {count}
            </Tag>
          ) : (
            '-'
          );
        },
      },
      {
        title: t('scanned'),
        dataIndex: 'scannedCode',
        key: 'scannedCode',
        width: 120,
        render: (value, record) => {
          if (!value) {
            return <span className="scan-status not-scanned">-</span>;
          }
          return (
            <Popover
              title={t('scannedCode')}
              content={
                <div className="scanned-code-popover">
                  <div className="code">{value}</div>
                  {record.scannedCodeType && <div className="type">Type: {record.scannedCodeType}</div>}
                  {record.scannedAt && (
                    <div className="time">Scanned: {moment(record.scannedAt).format(DAYTIME_FORMAT)}</div>
                  )}
                </div>
              }
            >
              <span className="scan-status scanned">Scanned</span>
            </Popover>
          );
        },
      },
      {
        title: tc('status'),
        dataIndex: 'status',
        key: 'status',
        fixed: 'right',
        width: 200,
        className: 'border-left',
        render: (value, record) => {
          return value === ORDER_STATUSES.ERROR && record.errorMessages?.length > 0 ? (
            <Popover
              title={t('errorDetails')}
              content={
                <div className="popupErrorMessages">
                  {record.errorMessages.map((error, index) => {
                    return (
                      <div key={index} className="item">
                        {error}
                      </div>
                    );
                  })}
                </div>
              }
            >
              <span className={'status order ' + value.toLowerCase()}>{tc(`orderStatuses.${value}`)}</span>
            </Popover>
          ) : (
            <span className={'status order ' + value.toLowerCase()}>{tc(`orderStatuses.${value}`)}</span>
          );
        },
      },
    ];

    if (withAction) {
      cols.push({
        key: 'action',
        width: 120,
        fixed: 'right',
        render(record) {
          return withAction(record);
        },
      });
    }

    return cols;
  }, [withAction]);

  const columnConfig = useColumnConfig('orders-columns', allColumns, FIXED_KEYS);

  // Expose openDrawer to parent via ref
  if (configRef) {
    configRef.current = { openDrawer: columnConfig.openDrawer, pageSize: columnConfig.pageSize };
  }

  const displayColumns = isMobile
    ? columnConfig.visibleColumns.filter((col) => !MOBILE_HIDDEN_KEYS.includes(col.key || col.dataIndex))
    : columnConfig.visibleColumns;

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  return (
    <div className="table-orders table-common">
      <Table
        columns={displayColumns}
        rowSelection={rowSelection}
        dataSource={dataSource}
        scroll={{ x: 'max-content' }}
        // bordered
        {...props}
      />
      <ColumnConfigDrawer
        open={columnConfig.drawerOpen}
        onClose={columnConfig.closeDrawer}
        columns={columnConfig.orderedAllColumns}
        hidden={columnConfig.hidden}
        fixedKeys={FIXED_KEYS}
        onToggle={columnConfig.toggleColumn}
        onMove={columnConfig.moveColumn}
        onReset={columnConfig.resetConfig}
        pageSize={columnConfig.pageSize}
        onPageSizeChange={(size) => {
          columnConfig.setPageSize(size);
          onPageSizeChangeProp?.(size);
        }}
      />
    </div>
  );
}
